import { colorsPalettes } from "@similarweb/styles";
import { PlainTooltip, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { CompanyLogo } from "app/pages/sales-intelligence/common-components/CompanyLogo/CompanyLogo";
import useTruncatedTextTooltip from "app/pages/sales-intelligence/hooks/useTruncatedTextTooltip";
import { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import type { CompanyCard } from "app/pages/sales-intelligence/modules/company-analysis/types/company";
import LeadScoreAgentContainer from "app/pages/sales-intelligence/modules/lead-scoring-agent/containers/lead-score-agent/LeadScoreAgentContainer";
import CRMIntegration from "app/pages/sales-intelligence/pages/company-analysis/components/cards/company-card/CRMIntegration/CRMIntegration";
import AddWebsiteToListButtonContainer from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/AddToListButton/AddWebsiteToListButtonContainer";

import {
    StyledAddWebsiteToListButton,
    StyledCompanyName,
    StyledExternalLink,
    StyledExternalLinkIcon,
    StyledLinkedinIcon,
} from "./styles";

const CompanyCardHeader = ({ company }: { company: CompanyCard | undefined }) => {
    const { params } = useCurrentStateAndParamsWithFixedDomain();
    const { isTooltipEnabled, refCallback } = useTruncatedTextTooltip();

    const handleLinkedinClick = () => {
        companyAnalysisTrackingService.widgets.trackLinkedinClick(company?.name ?? "", company?.linkedinUrl ?? "");
    };
    const handleWebsiteClick = () => {
        companyAnalysisTrackingService.widgets.trackWebsiteClick(company?.name ?? "", `https://${params.key}`);
    };

    const domain = company?.mainDomain;
    const { score, triggeredRules } = company?.leadPriority || {};
    const resolvedScore = score ?? LeadPriorityScore.Unlock;

    const handleLeadScoreClick = () => {
        companyAnalysisTrackingService.trackLeadPriorityClick(resolvedScore);
    };

    return (
        <Flex
            alignItems="center"
            justifyContent="space-between"
        >
            <Flex
                alignItems="center"
                gap="16px"
                maxWidth={"calc(100% - 30px)"}
            >
                <Flex
                    alignItems="center"
                    gap="8px"
                    overflow="hidden"
                >
                    <CompanyLogo src={company?.favIcon} />
                    <PlainTooltip
                        tooltipContent={company?.name}
                        placement="top"
                        enabled={isTooltipEnabled}
                    >
                        <StyledCompanyName
                            data-automation="company-name"
                            ref={refCallback}
                        >
                            {company?.name || params.key}
                        </StyledCompanyName>
                    </PlainTooltip>
                </Flex>
                <LeadScoreAgentContainer
                    score={score}
                    domain={domain || ""}
                    triggeredRules={triggeredRules}
                    onClick={handleLeadScoreClick}
                />
                <CRMIntegration companyInfo={company} />
                <StyledAddWebsiteToListButton>
                    <AddWebsiteToListButtonContainer domain={params.key} />
                </StyledAddWebsiteToListButton>
            </Flex>
            <Tooltip content={translate("si.company-analysis.company-card.mainDomain.tooltip")}>
                <StyledExternalLink
                    href={`https://${params.key}`}
                    target="_blank"
                    onMouseDown={handleWebsiteClick}
                >
                    <StyledExternalLinkIcon
                        iconName="link-out"
                        iconColor={colorsPalettes.carbon[300]}
                        iconSize="xs"
                        type="flat"
                        placement="right"
                    >
                        {params.key}
                    </StyledExternalLinkIcon>
                </StyledExternalLink>
            </Tooltip>
            {company?.linkedinUrl ? (
                <Tooltip content={translate("si.company-analysis.company-card.linkedin.tooltip")}>
                    <a
                        href={company.linkedinUrl}
                        target="_blank"
                        onMouseDown={handleLinkedinClick}
                    >
                        <StyledLinkedinIcon
                            iconName="social-linkedin"
                            iconSize="sm"
                            type="flat"
                        />
                    </a>
                </Tooltip>
            ) : null}
        </Flex>
    );
};

export default CompanyCardHeader;
